/* eslint-disable @typescript-eslint/restrict-template-expressions, @typescript-eslint/strict-boolean-expressions */

import { ReactNode } from 'react';
import { SetterOrUpdater } from 'recoil';
import { Controls, ControlsProps } from '../../shared/Controls';
import { ExpandableContentButton, InfoButton } from 'components/shared';
import { alpha, Box, Stack, Typography, useTheme } from '@mui/material';
import { SymSelectorSettings } from 'types';
import { FoldableContentBtn } from 'components/shared/FoldableContentBtn';
import SymSelector from 'pages/components/Quadrant/Editor/SymSelector';

interface IndicesHeaderProps {
  type: string;
  title: string;
  expandable?: boolean;
  controllerProps?: ControlsProps;
  customController?: JSX.Element;
  hideTitle?: boolean;
  symSelectorSettings?: SymSelectorSettings;
  symbol: string;
  shouldHideBtns?: boolean;
  isUnfolded?: boolean;
  setIsUnfolded?: SetterOrUpdater<boolean>;
  /**
   * Host-provided extras rendered alongside the built-in InfoButton /
   * ExpandableContentButton so the entire header reads as one row. Used
   * by Canvas* wrappers to slot in the symbol-group link button.
   */
  headerExtras?: ReactNode;
}

export const IndicesHeader = ({
  type,
  title,
  expandable,
  controllerProps,
  customController,
  hideTitle,
  isUnfolded,
  setIsUnfolded,
  shouldHideBtns,
  symSelectorSettings,
  symbol,
  headerExtras,
}: IndicesHeaderProps) => {
  const theme = useTheme();
  const contentController = (
    <Stack
      direction="row"
      gap={3}
      justifyContent="space-between"
      alignItems="center"
    >
      {symSelectorSettings && (
        <SymSelector
          value={symSelectorSettings.value}
          options={symSelectorSettings.options}
          onChange={symSelectorSettings.onChange}
          loading={symSelectorSettings.loading}
        />
      )}
      <Stack direction="row" alignItems="center" gap={1}>
        {controllerProps && <Controls {...controllerProps} />}
        {customController}
      </Stack>
    </Stack>
  );

  return (
    <Stack
      gap={1}
      sx={{
        cursor:
          isUnfolded != null && setIsUnfolded != null ? 'pointer' : 'inherit',
        padding: 2,
        borderRadius: 1,
        ':hover': {
          backgroundColor:
            isUnfolded != null && setIsUnfolded != null
              ? alpha(theme.palette.primary.main, 0.03)
              : 'transparent',
        },
      }}
      onClick={(e) => {
        e.stopPropagation();
        if (isUnfolded != null && setIsUnfolded != null) {
          setIsUnfolded((prev) => !prev);
        }
      }}
    >
      <Stack
        direction="row"
        gap={1}
        alignItems="center"
        justifyContent="space-between"
      >
        {!hideTitle ? (
          typeof title === 'string' ? (
            <Typography
              variant="h2"
              color="text.primary"
              textTransform="capitalize"
              fontWeight={500}
              sx={{ cursor: 'default' }}
              onMouseDown={(e) => e.stopPropagation()}
              onMouseEnter={(e) => e.stopPropagation()}
            >
              {`${symbol} ${title}`}
            </Typography>
          ) : (
            `${symbol} ${title}`
          )
        ) : undefined}

        {/* pushes settings to the right when title is hidden */}
        <Box flex={1} />

        <Stack direction="row" alignItems="center" gap={2}>
          {!shouldHideBtns && (
            <>
              <Box
                sx={{
                  display: {
                    xs: 'none',
                    sm: 'flex',
                  },
                }}
              >
                {contentController}
              </Box>

              <InfoButton key={`${type}-info`} articleKey={type} />
              {headerExtras}
              {expandable && <ExpandableContentButton type={type} />}
            </>
          )}

          {setIsUnfolded != null && isUnfolded != null && (
            <FoldableContentBtn
              isUnfolded={isUnfolded}
              setIsUnfolded={setIsUnfolded}
            />
          )}
        </Stack>
      </Stack>
      {!shouldHideBtns && (
        <Box
          sx={{
            display: {
              xs: 'flex',
              sm: 'none',
            },
          }}
        >
          {contentController}
        </Box>
      )}
    </Stack>
  );
};
